'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { TDynamicData } from '../../types/dynamic-data';
import { Carousel } from '../ui/carousel';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useState } from 'react';
import { LightboxComponent } from '@eliadmin/core/src/components/lightbox/lightbox-component';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { Heading } from './heading';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export const MenuSection = ({ menu }: { menu: TDynamicData[] }) => {
  const menuSK = menu.find(item => item?.id === 54)?.fields?.[0]?.images;
  const menuHU = menu.find(item => item?.id === 55)?.fields?.[0]?.images;
  const menuEN = menu.find(item => item?.id === 56)?.fields?.[0]?.images;

  const [selectedMenu, setSelectedMenu] = useState('SK menu');

  const { isOpen, setIsOpen } = useDisclosure(false);
  const [imagesSorted, setImagesSorted] = useState<{ url: string; name: string }[]>([]);

  const handleOpenGallery = (images: { url: string; name: string }[], index: number) => () => {
    if (Array.isArray(images) && index >= 0 && index < images.length) {
      const newImages = [...images.slice(index), ...images.slice(0, index)];
      setImagesSorted(newImages);
      setIsOpen(true);
    }
  };

  const imagesByMenu: Record<string, { url: string; name: string }[]> = {
    'SK menu': (menuSK as any) ?? [],
    'HU menu': (menuHU as any) ?? [],
    'EN menu': (menuEN as any) ?? [],
  };
  const currentImages = imagesByMenu[selectedMenu] ?? [];

  return (
    <section className="custom-section my-12 md:mt-24">
      <LightboxComponent isOpen={isOpen} setIsOpen={setIsOpen} images={imagesSorted} />
      <div className="container flex flex-col items-center justify-center gap-8 px-0 sm:px-4 md:px-6">
        <Heading
          title="Jedálny lístok"
          subtitle="Menu"
          className={{ container: 'items-center justify-center text-center' }}
        />
        <div className="flex h-[56px] max-w-fit gap-1 rounded-md bg-secondary p-1">
          <MenuSectionButton
            label="SK menu"
            active={selectedMenu === 'SK menu'}
            onClick={() => setSelectedMenu('SK menu')}
          />
          <MenuSectionButton
            label="HU menu"
            active={selectedMenu === 'HU menu'}
            onClick={() => setSelectedMenu('HU menu')}
          />
          <MenuSectionButton
            label="EN menu"
            active={selectedMenu === 'EN menu'}
            onClick={() => setSelectedMenu('EN menu')}
          />
        </div>
        {currentImages.length > 0 ? (
          <Carousel
            slides={
              currentImages?.map((item: any, index: number) => {
                return (
                  <div
                    key={item?.id || item?.url || index}
                    onClick={handleOpenGallery(currentImages as any, index)}
                    className="relative h-full w-full cursor-pointer"
                  >
                    <Image
                      src={item.url}
                      alt={resolveImageAlt(item, item?.name)}
                      width={0}
                      height={0}
                      sizes="100vw"
                      className="h-full max-h-[400px] w-auto object-cover object-center sm:max-h-[500px] lg:max-h-[600px]"
                    />
                  </div>
                );
              }) ?? []
            }
            showArrows
            className={{
              main: 'gap-2',
              container: 'gap-2',
              slide: 'max-w-fit',
            }}
            options={{
              dragFree: true,
            }}
          />
        ) : (
          <div className="flex h-[400px] w-full items-center justify-center sm:h-[500px] lg:h-[600px]">
            <p className="font-heading text-lg sm:text-xl xl:text-2xl">No menu available</p>
          </div>
        )}
      </div>
    </section>
  );
};

const MenuSectionButton = ({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) => {
  return (
    <div
      onClick={active ? undefined : onClick}
      className={cn(
        'group relative flex h-12 w-[120px] cursor-pointer items-center justify-center overflow-hidden rounded-md bg-transparent text-primary transition-all duration-200 ease-out sm:w-[132px]',
        'hover:bg-primary/90 hover:text-white',
        active && 'cursor-default bg-primary text-white shadow-sm',
      )}
    >
      {/* animated background highlight */}
      <span
        className={cn(
          'absolute inset-0 -z-10 scale-x-0 rounded-md bg-primary transition-transform duration-200 ease-out',
          active ? 'scale-x-100' : 'group-hover:scale-x-100',
        )}
      />
      <p className="font-sans">{label}</p>
    </div>
  );
};
